import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAppServices } from "hook/services";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaEnvelope,
  FaEye,
  FaRegCalendarAlt,
  FaSearch,
  FaStar,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import userImage from "../../../../assets/images/userImage.png";

const STATUS_META = {
  Pending: { accent: "#f59e0b", bg: "rgba(245,158,11,.12)", border: "rgba(245,158,11,.24)" },
  Approved: { accent: "#16a34a", bg: "rgba(22,163,74,.12)", border: "rgba(22,163,74,.24)" },
  Rejected: { accent: "#dc2626", bg: "rgba(220,38,38,.12)", border: "rgba(220,38,38,.24)" },
};

const formatDateTime = (value) =>
  new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const normalizeStatus = (status) => {
  const normalized = (status || "pending").toString().trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "rejected") return "Rejected";
  return "Pending";
};

const ReviewBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.Pending;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 96,
        padding: "7px 12px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        color: meta.accent,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
      }}
    >
      {status}
    </span>
  );
};

const reviews = ({ data, onLoad, agency }) => {
  const AppService = useAppServices();
  // const [agency] = useAgencyInfo(); // remove since passed as prop

  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [activeFilter, setActiveFilter] = useState("Pending");
  const [search, setSearch] = useState("");
  const [selectedReview, setSelectedReview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  useEffect(() => {
    if (data) {
      // If data is provided, use it
      const normalized = data.map((review) => ({
        _id: review._id,
        image: review.user_id?.profile_image || "",
        userName:
          review.user_id?.first_name && review.user_id?.last_name
            ? `${review.user_id.first_name} ${review.user_id.last_name}`
            : "Unknown User",
        userEmail: review.user_id?.email || review.userEmail || "",
        rating: Number(review.rating) || 0,
        message: review.content || review.review || review.message || "",
        submittedAt: review.createdAt || review.updatedAt || new Date().toISOString(),
        status: normalizeStatus(review.status),
        businessName: review?.business_id?.first_name || "Unknown Business",
      }));
      setReviews(normalized);
      setLoading(false);
    } else {
      // Else fetch
      const loadReviews = async () => {
        setLoading(true);
        try {
          const query = agency?._id ? `agency_id=${agency._id}` : "";
          const { response, error } = await AppService.reviews.get({ query });

          if (error) {
            toast.error(error?.message || "Failed to load reviews");
            setReviews([]);
            setLoading(false);
            return;
          }

          const data = Array.isArray(response?.data) ? response.data : [];
          const normalized = data.map((review) => ({
            _id: review._id,
            image: review.user_id?.profile_image || "",
            userName:
              review.user_id?.first_name && review.user_id?.last_name
                ? `${review.user_id.first_name} ${review.user_id.last_name}`
                : "Unknown User",
            userEmail: review.user_id?.email || review.userEmail || "",
            rating: Number(review.rating) || 0,
            message: review.content || review.review || review.message || "",
            submittedAt: review.createdAt || review.updatedAt || new Date().toISOString(),
            status: normalizeStatus(review.status),
            businessName: review?.business_id?.first_name || "Unknown Business",
          }));

          setReviews(normalized);
        } catch (err) {
          console.error(err);
          toast.error("Unable to fetch reviews");
          setReviews([]);
        } finally {
          setLoading(false);
        }
      };

      loadReviews();
    }
  }, [agency, data]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, search]);

  const filteredReviews = reviews.filter((review) => {
    const normalizedSearch = search.trim().toLowerCase();
    const matchesStatus = activeFilter === "All" || review.status === activeFilter;
    const matchesSearch =
      !normalizedSearch ||
      review.userName.toLowerCase().includes(normalizedSearch) ||
      review.message.toLowerCase().includes(normalizedSearch);

    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedReviews = filteredReviews.slice((safePage - 1) * pageSize, safePage * pageSize);

  const filterItems = [
    { label: "Pending", count: reviews.filter((item) => item.status === "Pending").length },
    { label: "Approved", count: reviews.filter((item) => item.status === "Approved").length },
    { label: "Rejected", count: reviews.filter((item) => item.status === "Rejected").length },
    { label: "All", count: reviews.length },
  ];

  const updateReviewStatus = async (reviewId, status) => {
    const safeStatus = normalizeStatus(status);

    const optimistic = (prev) =>
      prev.map((review) => (review._id === reviewId ? { ...review, status: safeStatus } : review));

    setReviews((prev) => optimistic(prev));
    setSelectedReview((prev) =>
      prev && prev._id === reviewId ? { ...prev, status: safeStatus } : prev
    );

    try {
      const payload = { id: reviewId, status: safeStatus.toLowerCase() };
      const { response, error } = await AppService.reviews.updateStatus({ payload });

      if (error) {
        throw new Error(error?.message || "Failed to update status");
      }

      const updated = response?.data;
      if (updated) {
        setReviews((prev) =>
          prev.map((review) =>
            review._id === reviewId
              ? {
                  ...review,
                  status: normalizeStatus(updated.status),
                  businessName: updated.business_id?.name || review.businessName,
                }
              : review
          )
        );
        setSelectedReview((prev) =>
          prev && prev._id === reviewId
            ? { ...prev, status: normalizeStatus(updated.status) }
            : prev
        );
      }

      toast.success(`Review ${safeStatus.toLowerCase()} successfully`);
    } catch (err) {
      toast.error(err.message || "Unable to update review status");
      // rollback if needed
      setReviews((prev) =>
        prev.map((review) => (review._id === reviewId ? { ...review, status: "Pending" } : review))
      );
      setSelectedReview((prev) =>
        prev && prev._id === reviewId ? { ...prev, status: "Pending" } : prev
      );
    }
  };

  const renderStars = (rating) => (
    <div style={{ display: "flex", gap: 4 }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <FaStar
          key={`${rating}-${index}`}
          size={14}
          color={index < rating ? "#fbbf24" : "#d1d5db"}
        />
      ))}
    </div>
  );

  return (
    <>
      <style>{`
        .review-shell{display:grid;grid-template-columns:250px minmax(0,1fr);gap:24px;font-family:Poppins,sans-serif}
        .review-sidebar{background:#f4f5f7;color:#0f172a;border-radius:24px;padding:24px;box-shadow:0 20px 50px rgba(0,0,0,.08)}
        .review-brand{display:flex;align-items:center;gap:12px;margin-bottom:22px}.review-logo{width:46px;height:46px;border-radius:14px;background:#1d4ed8;display:flex;align-items:center;justify-content:center;font-weight:700;color:#fff}
        .review-copy{color:#334155;font-size:13px;line-height:1.6;margin-bottom:20px}
        .review-nav{width:100%;border:1px solid #dbe2ea;background:#fff;color:#0f172a;border-radius:16px;padding:14px 16px;margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;transition:.2s}
        .review-nav:hover{background:#eef2ff;border-color:#c7d2fe;transform:translateX(2px)}
        .review-nav.active{background:#e0e7ff;border-color:#a5b4fc;transform:none}
        .review-main{display:flex;flex-direction:column;gap:20px}.review-header{display:flex;justify-content:space-between;gap:16px;align-items:center;padding:24px 28px;border-radius:24px;background:linear-gradient(180deg,#f8fafc 0%,#fff 100%);border:1px solid #e5e7eb}
        .review-header h2{margin:0 0 6px;font-size:28px;color:#0f172a}.review-header p{margin:0;color:#64748b;font-size:14px}
        .review-search{position:relative;min-width:0;max-width:275px;flex:0 1 275px}.review-search input{width:100%;max-width:275px;height:36px;border-radius:5px;border:1px solid #dbe3ef;padding:0 12px 0 40px;outline:none;font-size:14px;box-sizing:border-box}.review-search svg{position:absolute;top:50%;left:12px;transform:translateY(-50%);color:#94a3b8}
        .review-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}.review-stat{border:1px solid #edf2f7;border-radius:22px;background:#fff;padding:20px;box-shadow:0 14px 35px rgba(15,23,42,.06)}
        .review-stat-label{color:#64748b;font-size:13px;font-weight:600;margin-bottom:14px}.review-stat-value{display:flex;align-items:center;justify-content:space-between;font-size:28px;font-weight:700;color:#0f172a}
        .review-card{border:1px solid #edf2f7;border-radius:24px;background:#fff;box-shadow:0 18px 40px rgba(15,23,42,.06)}.review-card-head{display:flex;justify-content:space-between;gap:12px;padding:22px 24px;border-bottom:1px solid #edf2f7}
        .review-card-head h3{margin:0;color:#0f172a;font-size:18px}.review-card-head p{margin:6px 0 0;color:#64748b;font-size:13px}
        .review-table-wrap{width:100%;overflow-x:auto}.review-table{width:100%;min-width:920px;border-collapse:separate;border-spacing:0}.review-table th{padding:16px 24px;color:#64748b;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;border-bottom:1px solid #edf2f7;background:#fbfdff;text-align:left}
        .review-table td{padding:18px 24px;border-bottom:1px solid #f1f5f9;color:#334155;font-size:14px;vertical-align:top}.review-table tr:hover{background:#f8fbff}
        .user-cell{display:flex;gap:12px;align-items:center}.user-avatar{width:42px;height:42px;border-radius:14px;background:linear-gradient(135deg,#dbeafe 0%,#ede9fe 100%);color:#1d4ed8;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .user-name{color:#0f172a;font-weight:700;margin-bottom:4px}.user-email{color:#64748b;font-size:12px}.message-cell{max-width:360px;line-height:1.6}
        .action-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap}.action-btn{border:none;border-radius:12px;padding:10px 14px;font-size:13px;font-weight:700;display:inline-flex;align-items:center;gap:8px;cursor:pointer;transition:.2s}
        .action-btn:hover{transform:translateY(-1px);box-shadow:0 8px 18px rgba(15,23,42,.12)}.approve{background:#dcfce7;color:#15803d}.reject{background:#fee2e2;color:#b91c1c}.view{background:#eff6ff;color:#1d4ed8}.ghost{background:#f8fafc;color:#334155;border:1px solid #e2e8f0}
        .review-state{padding:56px 24px;text-align:center;color:#64748b}.review-state h3{color:#0f172a;font-size:20px;margin:16px 0 8px}.review-state p{margin:0;font-size:14px}
        .spinner{width:40px;height:40px;border-radius:999px;border:3px solid #dbeafe;border-top-color:#2563eb;margin:0 auto;animation:review-spin .8s linear infinite}
        .review-mobile{display:none;padding:16px;gap:14px}.review-mobile-card{border:1px solid #e5e7eb;border-radius:20px;padding:18px;background:linear-gradient(180deg,#fff 0%,#f8fbff 100%)}
        .review-mobile-top{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:12px}.meta-line{display:flex;align-items:center;gap:8px;color:#64748b;font-size:13px;margin-top:10px}
        .review-pagination{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:18px 24px}.review-pagination p{margin:0;color:#64748b;font-size:13px}.page-actions{display:flex;gap:8px}
        .page-btn{width:38px;height:38px;border-radius:12px;border:1px solid #dbe3ef;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer}.page-btn:disabled{opacity:.5;cursor:not-allowed}
        .drawer-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.34);z-index:1200}.drawer{position:fixed;top:0;right:0;width:min(460px,100%);height:100vh;background:#fff;z-index:1201;box-shadow:-20px 0 50px rgba(15,23,42,.14);display:flex;flex-direction:column;animation:drawer-in .24s ease}
        .drawer-head{padding:22px 24px;border-bottom:1px solid #edf2f7;display:flex;justify-content:space-between;gap:12px}.drawer-head h3{margin:0 0 6px;color:#0f172a;font-size:20px}.drawer-head p{margin:0;color:#64748b;font-size:13px}
        .drawer-close{width:38px;height:38px;border-radius:12px;border:none;background:#f8fafc;cursor:pointer}.drawer-body{padding:24px;overflow-y:auto}.detail-card{border:1px solid #e5e7eb;border-radius:20px;padding:20px;margin-bottom:18px}
        .detail-user{display:flex;gap:14px;align-items:center;margin-bottom:18px}.detail-avatar{width:56px;height:56px;border-radius:18px;background:linear-gradient(135deg,#e0f2fe 0%,#ede9fe 100%);color:#1d4ed8;display:flex;align-items:center;justify-content:center}
        .detail-label{color:#64748b;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}.detail-text{color:#334155;font-size:14px;line-height:1.7}.drawer-footer{padding:20px 24px;border-top:1px solid #edf2f7;display:flex;gap:10px;background:#fff}
        @keyframes review-spin{to{transform:rotate(360deg)}}@keyframes drawer-in{from{transform:translateX(100%)}to{transform:translateX(0)}}
        @media (max-width:1200px){.review-shell{grid-template-columns:1fr}.review-sidebar-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.review-nav{margin-bottom:0}}
        @media (max-width:900px){.review-header{flex-direction:column;align-items:stretch}.review-search{min-width:0;flex:1 1 auto}.review-stats{grid-template-columns:1fr}.review-table-wrap{display:none}.review-mobile{display:grid}}
        @media (max-width:640px){.review-sidebar-grid{grid-template-columns:1fr}.review-header{padding:20px}.review-header h2{font-size:24px}.drawer-footer{flex-direction:column}}
      `}</style>
      <div className="review-shell">
        <aside className="review-sidebar">
          <div className="review-brand">
            <div className="review-logo">RM</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Review Hub</div>
              <div style={{ color: "#212121", fontSize: 13 }}>Admin Workspace</div>
            </div>
          </div>
          <p className="review-copy">
            Monitor incoming feedback, triage pending submissions, and keep moderation moving with a
            fast approval flow.
          </p>
          <div className="review-sidebar-grid">
            {filterItems.map((item) => (
              <button
                key={item.label}
                className={`review-nav ${activeFilter === item.label ? "active" : ""}`}
                onClick={() => setActiveFilter(item.label)}
              >
                <span>{item.label} Reviews</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{item.count}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="review-main">
          <div className="review-header">
            <div>
              <h2>Review Management</h2>
              <p>
                Moderate community feedback, review new submissions, and keep published testimonials
                high quality.
              </p>
            </div>
            <div className="review-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by reviewer or review text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="review-stats">
            {["Pending", "Approved", "Rejected"].map((status) => (
              <div className="review-stat" key={status}>
                <div className="review-stat-label">
                  {status === "Pending" ? "Pending Queue" : status}
                </div>
                <div className="review-stat-value">
                  <span>{reviews.filter((item) => item.status === status).length}</span>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      background: STATUS_META[status].accent,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="review-card">
            <div className="review-card-head">
              <div>
                <h3>{activeFilter === "All" ? "All Reviews" : `${activeFilter} Reviews`}</h3>
                <p>
                  Showing {filteredReviews.length} review{filteredReviews.length === 1 ? "" : "s"}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="review-state">
                <div className="spinner" />
                <h3>Loading reviews</h3>
                <p>Preparing the moderation queue for you.</p>
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="review-state">
                <FaClock size={34} color="#94a3b8" />
                <h3>No reviews found</h3>
                <p>Try a different status filter or search term.</p>
              </div>
            ) : (
              <>
                <div className="review-table-wrap">
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Reviewer</th>
                        <th>Business</th>
                        <th>Rating</th>
                        <th>Review</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReviews.map((review) => (
                        <tr key={review._id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                  <img
                                    src={review.image || userImage}
                                    alt={review.userName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "inherit",
                                      objectFit: "cover",
                                    }}
                                    />
                              </div>
                              <div>
                                <div className="user-name">{review.userName}</div>
                                <div className="user-email">
                                  {review.userEmail || "No email provided"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{review.businessName || "N/A"}</td>
                          <td>{renderStars(review.rating)}</td>
                          <td className="message-cell">
                            {review.message.length > 120
                              ? `${review.message.slice(0, 120)}...`
                              : review.message}
                          </td>
                          <td>{formatDateTime(review.submittedAt)}</td>
                          <td>
                            <ReviewBadge status={review.status} />
                          </td>
                          <td>
                            <div className="action-row">
                              <button
                                className="action-btn approve"
                                onClick={() => updateReviewStatus(review._id, "Approved")}
                              >
                                <FaCheck size={12} /> Approve
                              </button>
                              <button
                                className="action-btn reject"
                                onClick={() => updateReviewStatus(review._id, "Rejected")}
                              >
                                <FaTimes size={12} /> Reject
                              </button>
                              <button
                                className="action-btn view"
                                onClick={() => setSelectedReview(review)}
                              >
                                <FaEye size={12} /> Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="review-mobile">
                  {paginatedReviews.map((review) => (
                    <div className="review-mobile-card" key={review._id}>
                      <div className="review-mobile-top">
                        <div>
                          <div className="user-name">{review.userName}</div>
                          <div className="user-email">
                            {review.userEmail || "No email provided"}
                          </div>
                          <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                            Business: {review.businessName || "N/A"}
                          </div>
                        </div>
                        <ReviewBadge status={review.status} />
                      </div>
                      {renderStars(review.rating)}
                      <div className="detail-text" style={{ marginTop: 12 }}>
                        {review.message}
                      </div>
                      <div className="meta-line">
                        <FaRegCalendarAlt />
                        <span>{formatDateTime(review.submittedAt)}</span>
                      </div>
                      <div className="action-row" style={{ marginTop: 16 }}>
                        <button
                          className="action-btn approve"
                          onClick={() => updateReviewStatus(review._id, "Approved")}
                        >
                          <FaCheck size={12} /> Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => updateReviewStatus(review._id, "Rejected")}
                        >
                          <FaTimes size={12} /> Reject
                        </button>
                        <button
                          className="action-btn view"
                          onClick={() => setSelectedReview(review)}
                        >
                          <FaEye size={12} /> View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="review-pagination">
                  <p>
                    Page {safePage} of {totalPages}
                  </p>
                  <div className="page-actions">
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                      disabled={safePage === 1}
                    >
                      <FaChevronLeft size={12} />
                    </button>
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      disabled={safePage === totalPages}
                    >
                      <FaChevronRight size={12} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
      {selectedReview && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelectedReview(null)} />
          <aside className="drawer">
            <div className="drawer-head">
              <div>
                <h3>Review Details</h3>
                <p>Inspect the full submission before moderating.</p>
              </div>
              <button className="drawer-close" onClick={() => setSelectedReview(null)}>
                <FaTimes size={14} />
              </button>
            </div>
            <div className="drawer-body">
              <div className="detail-card">
                <div className="detail-user">
                  <div className="detail-avatar">
                    {selectedReview.image ? (
                      <img
                        src={selectedReview.image}
                        alt={selectedReview.userName}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "inherit",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <FaUserCircle size={28} />
                    )}
                  </div>
                  <div>
                    <div className="user-name">{selectedReview.userName}</div>
                    <div className="user-email">
                      {selectedReview.userEmail || "No email provided"}
                    </div>
                  </div>
                </div>
                <div className="detail-label">Status</div>
                <div style={{ marginBottom: 18 }}>
                  <ReviewBadge status={selectedReview.status} />
                </div>
                <div className="detail-label">Rating</div>
                <div style={{ marginBottom: 18 }}>{renderStars(selectedReview.rating)}</div>
                <div className="detail-label">Submitted</div>
                <div className="meta-line" style={{ marginTop: 0, marginBottom: 18 }}>
                  <FaRegCalendarAlt />
                  <span>{formatDateTime(selectedReview.submittedAt)}</span>
                </div>
                <div className="meta-line" style={{ marginTop: 0 }}>
                  <FaEnvelope />
                  <span>{selectedReview.userEmail || "No email provided"}</span>
                </div>
              </div>
              <div className="detail-card">
                <div className="detail-label">Review Message</div>
                <div className="detail-text">{selectedReview.message}</div>
              </div>
            </div>
            <div className="drawer-footer">
              <button
                className="action-btn approve"
                onClick={() => updateReviewStatus(selectedReview._id, "Approved")}
              >
                <FaCheck size={12} /> Approve Review
              </button>
              <button
                className="action-btn reject"
                onClick={() => updateReviewStatus(selectedReview._id, "Rejected")}
              >
                <FaTimes size={12} /> Reject Review
              </button>
              <button className="action-btn ghost" onClick={() => setSelectedReview(null)}>
                Close
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default reviews;
